<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuth } from './stores/auth'
import { useSettings } from './stores/settings'
import { notificationsApi, nginxApi, certsApi, healthApi, analyzerApi } from './api'
import { useShortcuts } from './composables/useShortcuts'
import GlobalSearch from './components/GlobalSearch.vue'
import { ElMessageBox, ElMessage } from 'element-plus'

const route = useRoute()
const router = useRouter()
const auth = useAuth()
const settings = useSettings()

const showShell = computed(() => auth.token && route.meta.requiresAuth !== false)
const failedNotifCount = ref(0)
const expiringCertsCount = ref(0)
const downSitesCount = ref(0)
const analyzerIssuesCount = ref(0)
const analyzerHasError = ref(false)
const searchVisible = ref(false)

async function pollFailedNotifs() {
  if (!auth.isAdmin) return
  try {
    const r = await notificationsApi.listLog({ status: 'failed', limit: 5 })
    failedNotifCount.value = r.data.total
  } catch { /* ignore */ }
}

async function pollExpiringCerts() {
  if (!auth.isAdmin) return
  try {
    const r = await certsApi.expiry()
    expiringCertsCount.value = (r.data.certificates ?? []).filter(c => c.status !== 'ok').length
  } catch { /* ignore — endpoint is best-effort */ }
}

async function pollDownSites() {
  if (!auth.isAdmin) return
  try {
    const r = await healthApi.summary()
    downSitesCount.value = r.data.down ?? 0
  } catch { /* ignore */ }
}

async function pollAnalyzer() {
  if (!auth.isAdmin) return
  try {
    const r = await analyzerApi.scanAll({ lines: 200 })
    let total = 0
    let anyError = false
    for (const sugs of Object.values(r.data.results ?? {})) {
      total += sugs.length
      if (sugs.some(s => s.severity === 'error')) anyError = true
    }
    analyzerIssuesCount.value = total
    analyzerHasError.value = anyError
  } catch { /* ignore */ }
}

// Refresh the down-count every minute so a newly failed site surfaces on the
// sidebar without needing a page reload. App.vue lives for the whole session,
// so we don't bother cleaning up the timer.
onMounted(() => {
  if (auth.token && auth.isAdmin) {
    pollFailedNotifs()
    pollExpiringCerts()
    pollDownSites()
    pollAnalyzer()
    window.setInterval(pollDownSites, 60000)
    // Rescan every 5 minutes — the analyzer's rule engine is cheap, but
    // scanning every site still reads disk, so we don't go faster.
    window.setInterval(pollAnalyzer, 300000)
  }
})

function logout() {
  auth.logout()
  router.push('/login')
}

async function reloadNginx() {
  try {
    await ElMessageBox.confirm('Reload nginx now?', 'Confirm', { type: 'warning' })
  } catch { return }
  try {
    const r = await nginxApi.reload()
    r.data.ok ? ElMessage.success('nginx reloaded') : ElMessage.error(r.data.stderr || 'reload failed')
  } catch { ElMessage.error('reload failed') }
}

async function testNginx() {
  try {
    const r = await nginxApi.test()
    r.data.ok ? ElMessage.success('nginx -t OK') : ElMessage.error(r.data.stderr || 'test failed')
  } catch { ElMessage.error('test failed') }
}

useShortcuts([
  { key: 'k', ctrl: true,  handler: () => { searchVisible.value = true } },
  { key: 'r', alt: true, handler: reloadNginx },
  { key: 't', alt: true, handler: testNginx },
  { seq: ['g', 'd'], handler: () => router.push('/') },
  { seq: ['g', 's'], handler: () => router.push('/sites') },
  { seq: ['g', 'l'], handler: () => router.push('/logs') },
])
</script>

<template>
  <div v-if="showShell" class="app-shell">
    <el-container style="height: 100vh">
      <el-aside width="220px" class="sidebar">
        <div class="brand">nginx-ui</div>
        <el-menu
          :default-active="route.path"
          router
          class="side-menu"
          background-color="#1f2937"
          text-color="#d1d5db"
          active-text-color="#60a5fa"
        >
          <el-menu-item index="/">Dashboard</el-menu-item>
          <el-menu-item index="/sites">
            <span>Sites</span>
            <el-badge
              v-if="auth.isAdmin && downSitesCount > 0"
              :value="downSitesCount"
              type="danger"
              :max="99"
              style="margin-left: 8px"
              title="Sites currently failing health checks"
            />
          </el-menu-item>
          <el-menu-item index="/logs">Logs</el-menu-item>
          <el-menu-item index="/analyzer">
            <span>Log analyzer</span>
            <el-badge
              v-if="auth.isAdmin && analyzerIssuesCount > 0"
              :value="analyzerIssuesCount"
              :type="analyzerHasError ? 'danger' : 'warning'"
              :max="99"
              style="margin-left: 8px"
              :title="analyzerIssuesCount + ' detected issue(s) across your sites'"
            />
          </el-menu-item>

          <!-- Admin-only items -->
          <template v-if="auth.isAdmin">
            <el-menu-item index="/config">Config Files</el-menu-item>
            <el-menu-item index="/ssl">
              <span>SSL / Certs</span>
              <el-badge
                v-if="expiringCertsCount > 0"
                :value="expiringCertsCount"
                type="warning"
                :max="99"
                style="margin-left: 8px"
              />
            </el-menu-item>
            <el-menu-item index="/modsecurity">ModSecurity</el-menu-item>
            <el-menu-item index="/global">Global settings</el-menu-item>
            <el-menu-item index="/audit">Audit log</el-menu-item>
            <el-menu-item index="/scheduler">Scheduler</el-menu-item>
            <el-menu-item index="/backup">Backup &amp; restore</el-menu-item>
            <el-menu-item index="/users">Users</el-menu-item>
            <el-menu-item index="/notifications">Notifications</el-menu-item>
          </template>

          <el-menu-item index="/account">Account</el-menu-item>
          <el-menu-item index="/help">Help</el-menu-item>
        </el-menu>
      </el-aside>

      <el-container>
        <el-header class="topbar">
          <div>
            Signed in as <strong>{{ auth.user?.username }}</strong>
            <el-tag size="small" :type="auth.isAdmin ? 'danger' : 'primary'" style="margin-left: 8px">
              {{ auth.user?.role }}
            </el-tag>
          </div>
          <div style="display: flex; align-items: center; gap: 12px">
            <el-button size="small" @click="searchVisible = true" title="Search (Ctrl+K)">⌕ Search</el-button>
            <el-button size="small" @click="settings.toggleTheme()"
                       :title="settings.theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'">
              {{ settings.theme === 'dark' ? '☀' : '☾' }}
            </el-button>
            <el-badge v-if="auth.isAdmin" :value="failedNotifCount || undefined" type="danger" :hidden="failedNotifCount === 0">
              <el-button size="small" @click="router.push('/notifications')" :title="failedNotifCount > 0 ? failedNotifCount + ' failed notification(s)' : 'Notifications'">
                🔔
              </el-button>
            </el-badge>
            <el-button type="primary" plain size="small" @click="logout">Log out</el-button>
          </div>
        </el-header>
        <el-main>
          <router-view />
        </el-main>
      </el-container>
    </el-container>
  </div>
  <router-view v-else />

  <GlobalSearch v-model:visible="searchVisible" />
</template>

<style>
html, body, #app { height: 100%; margin: 0; }
body { background: var(--el-bg-color); color: var(--el-text-color-primary); }
.sidebar { background: #1f2937; color: #d1d5db; }
.brand { padding: 18px 20px; font-weight: 700; font-size: 18px; color: #fff; border-bottom: 1px solid #374151; }
.side-menu { border-right: none; }
.topbar {
  display: flex; justify-content: space-between; align-items: center;
  background: var(--el-bg-color); border-bottom: 1px solid var(--el-border-color);
}
.el-main { background: var(--el-bg-color-page); }
</style>
